/**
 * Turn the rows on screen into a CSV file, without asking the server for one.
 *
 * The admin already has four report endpoints that build CSVs server-side, and
 * this is not those: they answer a business question over a date range. This is
 * the other thing people want, which is *the list they are looking at*, in a
 * spreadsheet — a filtered set of thirty signups to hand to somebody, a page of
 * transactions to reconcile by eye. Today that is done by selecting the table
 * and pasting it, which loses every id, because ids are rendered short.
 *
 * It exports what the page fetched, not what it rendered, so ids come out whole.
 * The header row is the column labels, so the file reads like the screen.
 */

// Excel decides a cell is a formula if it starts with one of these, and will
// happily run it. A leading apostrophe is the standard defusing.
const RISKY = /^[=+\-@\t\r]/;

const cell = value => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();

  // Objects reach here from payloads that nest — a `{ AsDecimal }` percentage,
  // a `{ Number }` phone. JSON is not pretty but it is lossless, and inventing
  // a flattening rule per shape is how a column silently starts saying "[object
  // Object]".
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  const safe = RISKY.test(text) ? `'${text}` : text;

  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/**
 * @param {Array<[string, string]>} columns `[key, label]`, the screen's own.
 * @param {Array<object>} rows the payload, untouched.
 */
export const toCsv = (columns, rows) => [
  columns.map(([, label]) => cell(label)).join(','),
  ...rows.map(row => columns.map(([key]) => cell(row[key])).join(','))
].join('\r\n');

/**
 * Hands the browser a file. The BOM is not decoration: without it Excel reads
 * the file as the local codepage and every Danish name comes out as mojibake.
 */
export const downloadCsv = (filename, text) => {
  const blob = new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking immediately can beat the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** `signups-2026-08-19.csv` — named after what it is and when it was taken. */
export const csvName = prefix => `${prefix}-${new Date().toISOString().slice(0, 10)}.csv`;
